import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getAutocompleteGlobalSearcher } from '../../api/search';

interface BusinessSearcherAutocompleteProps {
  setBusiness: (business: any) => void;
  setSearched: (searched: boolean) => void;
}

const BusinessSearcherAutocomplete = (
  props: BusinessSearcherAutocompleteProps,
) => {
  const { setBusiness, setSearched } = props;
  const [searchType, setSearchType] = useState<'rut' | 'name'>('rut');
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [businessSearched, setBusinessSearched] = useState<any>({});
  const [autocompleteOptions, setAutocompleteOptions] = useState<any[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [inputValue, setInputValue] = useState('');

  const dispatch = useDispatch();

  // Función para manejar el autocomplete
  const handleAutocompleteInputChange = useCallback(
    async (inputValue: string, searchType: 'rut' | 'name') => {
      if (!inputValue.trim()) {
        setAutocompleteOptions([]);
        return;
      }

      setAutocompleteLoading(true);
      try {
        const label =
          searchType === 'rut' ? 'businessIdentifier' : 'businessName';
        const response = await getAutocompleteGlobalSearcher(dispatch, {
          label,
          input: inputValue,
        });
        console.log('autocomplete options', autocompleteOptions);
        console.log(response);

        if (response) {
          console.log('setting autocomplete options', response);
          setAutocompleteOptions(response);
        } else {
          console.log('No response received, setting empty options');
          setAutocompleteOptions([]);
        }
      } catch (error) {
        console.error('Error searching autocomplete options:', error);
        setAutocompleteOptions([]);
      } finally {
        setAutocompleteLoading(false);
      }
    },
    [dispatch],
  );

  // Función para manejar cambios en el autocomplete
  const handleChangeBusiness = (e: React.SyntheticEvent, newValue: any) => {
    setSearched(false);
    setBusinessSearched({});
    setSelectedBusiness(newValue);

    if (newValue) {
      setBusiness(newValue.object);
      setBusinessSearched(newValue);
      setSearched(true);
      // Actualizar inputValue para mostrar la opción seleccionada
      if (searchType === 'rut') {
        setInputValue(newValue.identifier || newValue.businessIdentifier || '');
      } else {
        setInputValue(newValue.name || newValue.businessName || '');
      }
    } else {
      setBusiness(null);
      setInputValue('');
    }
  };

  // Limpiar cuando cambia el tipo de búsqueda
  useEffect(() => {
    setSearched(false);
    setBusiness(null);
    setBusinessSearched({});
    setSelectedBusiness(null);
    setAutocompleteOptions([]);
    setInputValue('');
  }, [searchType]);

  // Cleanup timeout al desmontar el componente
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Debug: Monitorear cambios en autocompleteOptions
  useEffect(() => {}, [autocompleteOptions]);

  return (
    <div>
      <FormControl component="fieldset" style={{ marginBottom: '20px' }}>
        <FormLabel component="legend">Tipo de búsqueda</FormLabel>
        <RadioGroup
          value={searchType}
          onChange={(e) => setSearchType(e.target.value as 'rut' | 'name')}
          row
        >
          <FormControlLabel value="rut" control={<Radio />} label="Por RUT" />
          <FormControlLabel
            value="name"
            control={<Radio />}
            label="Por Nombre"
          />
        </RadioGroup>
      </FormControl>

      <div style={{ padding: '10px 0px' }}>
        <Autocomplete
          inputValue={inputValue}
          options={autocompleteOptions}
          getOptionLabel={(option) => {
            if (searchType === 'rut') {
              return option.label;
            }
            return option.label;
          }}
          value={selectedBusiness}
          onChange={handleChangeBusiness}
          onInputChange={(event, newInputValue, reason) => {
            setInputValue(newInputValue);
            // Solo buscar cuando el usuario está escribiendo
            if (reason === 'input') {
              // Limpiar timeout anterior si existe
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
              }

              // Crear nuevo timeout para debounce
              timeoutRef.current = setTimeout(() => {
                console.log(
                  'Timeout executed, calling handleAutocompleteInputChange',
                );
                void handleAutocompleteInputChange(newInputValue, searchType);
              }, 500);
            }
          }}
          loading={autocompleteLoading}
          freeSolo={false}
          openOnFocus={false}
          isOptionEqualToValue={(option, value) => {
            if (searchType === 'rut') {
              return (
                (option.identifier || option.businessIdentifier) ===
                (value.identifier || value.businessIdentifier)
              );
            }
            return (
              (option.name || option.businessName) ===
              (value.name || value.businessName)
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={searchType === 'rut' ? 'RUT' : 'Nombre de la empresa'}
              placeholder={
                searchType === 'rut'
                  ? 'Escribe el RUT de la empresa'
                  : 'Escribe el nombre de la empresa'
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {autocompleteLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <div>
                <Typography variant="body2">{option.label}</Typography>
              </div>
            </li>
          )}
          noOptionsText="No se encontraron empresas"
          loadingText="Buscando empresas..."
        />
      </div>

      {businessSearched.name && (
        <Typography
          variant="caption"
          style={{ marginTop: '10px', display: 'block' }}
        >
          Empresa encontrada: {businessSearched.name}
          {businessSearched.identifier &&
            ` (RUT: ${businessSearched.identifier})`}
        </Typography>
      )}
    </div>
  );
};

export default BusinessSearcherAutocomplete;
